import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
} from "@chakra-ui/react";
import { useState } from "react";
import { useMutation } from "react-query";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useKy } from "src/common/ky";
import { Loading } from "src/common/Loading";
import { Pagination } from "src/common/Pagination";
import { PageContent } from "src/layout/PageContent";
import {
  PageHeader,
  PageHeaderTabs,
  PageHeaderTitle,
} from "src/layout/PageHeader";
import { TabLink } from "src/layout/TabLink";
import { CELL_KITS_URL } from "src/routes/ProductUnits/CellKits";
import { IntercomsFilterBar } from "src/routes/ProductUnits/Intercoms/IntercomsFilterBar";
import { IntercomsTable } from "src/routes/ProductUnits/Intercoms/IntercomsTable";
import { useIntercomsQuery } from "src/routes/ProductUnits/Intercoms/queries";

export const INTERCOMS_URL = "/product-units/intercoms";

export const Intercoms = () => {
  const navigate = useNavigate();
  const ky = useKy();
  const [searchParams, setSearchParams] = useSearchParams();
  const [deletingUnitId, setDeletingUnitId] = useState<string | undefined>();
  const {
    isOpen: deleteModalIsOpen,
    onOpen: onOpenDeleteModal,
    onClose: onCloseDeleteModal,
  } = useDisclosure();

  const query = useIntercomsQuery();

  const deleteIntercom = useMutation(
    (id: string | undefined) => ky.delete(`intercoms/${id}`),
    {
      onSuccess: () => {
        query.refetch();
        setDeletingUnitId(undefined);
        onCloseDeleteModal();
      },
    }
  );

  return (
    <>
      <PageHeader buttons={<IntercomsFilterBar />}>
        <PageHeaderTitle>Intercom Inventory</PageHeaderTitle>
        <PageHeaderTabs>
          <TabLink key="intercoms" to={INTERCOMS_URL} label="Intercoms" end />
          <TabLink key="cell-kits" to={CELL_KITS_URL} label="Cell Kits" end />
        </PageHeaderTabs>
      </PageHeader>
      {!query.data ? (
        <Loading />
      ) : (
        <PageContent>
          <IntercomsTable
            intercoms={query.data.result}
            onDeleteIntercom={(intercom) => {
              setDeletingUnitId(intercom.intercomId);
              onOpenDeleteModal();
            }}
            onEditIntercom={(intercom) => {
              navigate(`/product-units/intercoms/${intercom.intercomId}/edit`);
            }}
          />

          <Pagination
            unitName="intercom unit"
            total={query.data.total}
            pageNumber={parseInt(searchParams.get("pageNumber") || "1")}
            pageSize={query.data.pageSize}
            onPageChange={(page) => {
              setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.set("pageNumber", page.toFixed(0));
                return next;
              });
            }}
          />

          <Modal
            isOpen={deleteModalIsOpen}
            onClose={() => {
              onCloseDeleteModal();
              setDeletingUnitId(undefined);
            }}
          >
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>Remove Intercom</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                Are you sure? You can&apos;t undo this action afterwards.
              </ModalBody>

              <ModalFooter>
                <Button
                  colorScheme="brand.lightGray"
                  color="gray.700"
                  mr={3}
                  onClick={() => {
                    onCloseDeleteModal();
                    setDeletingUnitId(undefined);
                  }}
                >
                  Close
                </Button>
                <Button
                  colorScheme="brand.red"
                  onClick={() => deleteIntercom.mutate(deletingUnitId)}
                  isDisabled={deleteIntercom.isLoading}
                >
                  Remove Intercom
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        </PageContent>
      )}
    </>
  );
};
